import moment from "moment";
import _ from "lodash";
import {
    ApplicationFilters,
    ApplicationFiltersStore,
} from "stores/ApplicationFilters/ApplicationFiltersStore";
import { DateReferenceOption } from "stores/ComponentStores/DatePickerComponentStore";
import RootStore from "stores/RootStore";
import { IAgentContext } from "./IContext";

export type AgentApplicationFilters = ApplicationFilters & {
    dateReference?: DateReferenceOption;
};

export function prepareFiltersForAgentContext(
    filters: ApplicationFilters,
): AgentApplicationFilters {
    const result = _.cloneDeep(filters) as AgentApplicationFilters;

    result.startDate = moment(result.startDate).format("YYYY-MM-DDTHH:mm:ss");
    result.endDate = moment(result.endDate).format("YYYY-MM-DDTHH:mm:ss");
    result.dateReference = result.dateReferenceOption;

    result.rbcFilterItems =
        result.rbcFilterItems?.filter(
            (item) => item.classifierIds.length > 0,
        ) ?? [];
    result.outlineSegmentFilterItems =
        result.outlineSegmentFilterItems?.filter(
            (item) => item.segments.length > 0,
        ) ?? [];
    result.modelResults = result.modelResults?.filter(
        (item) => item.results.length > 0,
    );

    if (result.extendedMetadata) {
        const extendedMetadata = { ...result.extendedMetadata };
        for (const [key, value] of Object.entries(extendedMetadata)) {
            if (!value) {
                delete extendedMetadata[key];
                continue;
            }
            extendedMetadata[key] = value.replace(/'/g, "''");
        }
        result.extendedMetadata = extendedMetadata;
    }

    // Zero is how the platform spells an unset bound on both range filters, so it is dropped rather
    // than sent. Otherwise KBo confirms a bound the user never chose. Dropping it does not widen
    // anything: a drilldown into a first bucket genuinely starts at zero, and there is nothing below
    // zero for the remaining bound to let back in.
    if (result.minCallDuration === 0) result.minCallDuration = undefined;
    if (result.maxCallDuration === 0) result.maxCallDuration = undefined;
    if (result.minMessageCount === 0) result.minMessageCount = undefined;
    if (result.maxMessageCount === 0) result.maxMessageCount = undefined;

    return result;
}

export enum UserSelectedContextType {
    AllConversations = "AllConversations",
    AppliedFilters = "AppliedFilters",
    ChartDataPoint = "ChartDataPoint",
    ProactiveInsight = "ProactiveInsight",
}

export interface IUserSelectedContext<T = unknown>
    extends IAgentContext<UserSelectedContextType, T> {
    label: string;
}

class AllConversationsContext implements IUserSelectedContext<undefined> {
    type: UserSelectedContextType.AllConversations;
    label: string;

    constructor() {
        this.type = UserSelectedContextType.AllConversations;
        this.label = "All Conversations";
    }

    public getValue(): undefined {}
}

class AppliedFiltersContext
    implements IUserSelectedContext<ApplicationFilters>
{
    type: UserSelectedContextType.AppliedFilters;
    label: string;

    constructor() {
        this.type = UserSelectedContextType.AppliedFilters;
        this.label = "Applied Filters";
    }

    public getValue(): ApplicationFilters {
        const store = RootStore().getStore(ApplicationFiltersStore);

        return prepareFiltersForAgentContext(store.toFilterObject());
    }
}

class ChartDataPointContext implements IUserSelectedContext<undefined> {
    type: UserSelectedContextType.ChartDataPoint;
    label: string;

    constructor() {
        this.type = UserSelectedContextType.ChartDataPoint;
        this.label = "Chart data point";
    }

    public getValue(): undefined {}
}

class ProactiveInsightContext implements IUserSelectedContext<undefined> {
    type: UserSelectedContextType.ProactiveInsight;
    label: string;

    constructor() {
        this.type = UserSelectedContextType.ProactiveInsight;
        this.label = "Proactive Insight";
    }

    public getValue(): undefined {}
}

export namespace UserSelectedContext {
    export const AllConversations = new AllConversationsContext();
    export const AppliedFilters = new AppliedFiltersContext();
    export const ChartDataPoint = new ChartDataPointContext();
    export const ProactiveInsight = new ProactiveInsightContext();
}
